import { useCallback, useEffect, useState } from 'react';

import { getQueue } from '../offline/offlineQueue';
import { RouteState } from '../types/surveillanceRoute';

/**
 * `operationName` de la mutación de ESTADO del RECORRIDO → estado que tendrá el
 * RV cuando esa mutación se reproduzca al reconectar.
 *
 * Todas son transiciones de estado del RECORRIDO matcheables por `routeId`. NO
 * hay caso erróneo: `CreateRouteSegment` solo es posible si el RV ya está
 * ≥ en-curso o pausado, así que `IN_PROGRESS` optimista o refleja un reanudar
 * real (pausado→en-curso, deseado) o es redundante (ya en-curso, no cambia
 * nada visible). El "no dispares con cada tramo" (feedback 2026-07-29) se
 * resuelve en el GATE de la pill (solo se bloquea si el RV se está CERRANDO),
 * no quitando el op — ver `RouteHeaderCard`.
 *
 * Quedan FUERA a propósito:
 * - `AddGpsPointsBatch`: los puntos GPS se encolan sin parar sin cambiar estado.
 * - `FinishRouteSegment`: su variable es el id del TRAMO, no del recorrido → no
 *   atribuible al RV desde la cola (lo cubre el RV optimista de
 *   `ft/rv-mapa-offline`).
 */
const OP_TO_ROUTE_STATE: Record<string, RouteState> = {
  StartSurveillanceRoute: RouteState.IN_PROGRESS,
  CreateRouteSegment: RouteState.IN_PROGRESS,
  FinishSurveillanceRoute: RouteState.PENDING_VALIDATION,
  ForceCloseSurveillanceRoute: RouteState.PENDING_VALIDATION,
  ValidateSurveillanceRoute: RouteState.END,
  AnnulSurveillanceRoute: RouteState.ANNULLED,
  ReopenSurveillanceRoute: RouteState.PAUSED,
};

/**
 * Estados en los que el RV se está CERRANDO (transición terminal encolada): con
 * uno de estos optimista NO debe poder re-cambiarse el estado (evita apilar dos
 * transiciones). Con in-progress/paused (patrulla/reanudar) la pill sigue
 * usable. Lo consume `RouteHeaderCard` para el gate de la pill.
 */
export const OPTIMISTIC_CLOSING_STATES: readonly RouteState[] = [
  RouteState.PENDING_VALIDATION,
  RouteState.END,
  RouteState.ANNULLED,
];

function opTargetsRoute(
  op: { variables?: unknown },
  routeId: string
): boolean {
  const vars = (op.variables ?? {}) as {
    id?: string;
    input?: { id?: string; surveillanceRouteId?: string };
  };
  return (
    vars.id === routeId || // Validate/Annul/Reopen usan `$id`
    vars.input?.id === routeId || // Start/Finish/ForceClose usan `input.id`
    vars.input?.surveillanceRouteId === routeId // CreateRouteSegment
  );
}

/**
 * Estado OPTIMISTA de un recorrido con una transición encolada offline.
 *
 * Sin red, las mutaciones de estado del RV se ENCOLAN (no se aplican local), y
 * las páginas RV leen un snapshot del loader → la tarjeta seguía mostrando el
 * estado ANTERIOR ("Trabajo en curso" + botón "Finalizar") aunque el operario
 * YA lo hubiera finalizado, dejándole re-disparar la acción y re-encolarla.
 *
 * Este hook deriva de la cola offline (`getQueue`) el estado que el RV TENDRÁ al
 * sincronizar, para que la UI (pill + botones Iniciar/Finalizar/Editar) lo
 * refleje ya. Se recalcula en cada `offline-queue-changed` y `offline-sync-complete`,
 * así que al drenar la cola vuelve a `null` solo (y el `useRevalidateOnSync` del
 * detalle refresca al estado REAL del servidor). Devuelve `null` si no hay
 * ninguna transición de estado encolada para este RV.
 */
export function useOptimisticRouteState(routeId?: string): RouteState | null {
  const [state, setState] = useState<RouteState | null>(null);

  const recompute = useCallback(async () => {
    if (!routeId) {
      setState(null);
      return;
    }
    const queue = await getQueue();
    // El ÚLTIMO op de estado encolado para esta ruta gana (el más reciente en
    // orden FIFO refleja la última intención del operario).
    let result: RouteState | null = null;
    for (const op of queue) {
      const mapped = OP_TO_ROUTE_STATE[op.operationName];
      if (mapped && opTargetsRoute(op, routeId)) result = mapped;
    }
    setState(result);
  }, [routeId]);

  useEffect(() => {
    void recompute();
    window.addEventListener('offline-queue-changed', recompute);
    window.addEventListener('offline-sync-complete', recompute);
    return () => {
      window.removeEventListener('offline-queue-changed', recompute);
      window.removeEventListener('offline-sync-complete', recompute);
    };
  }, [recompute]);

  return state;
}
